import { computed, observable, action } from 'mobx';
import first from 'lodash/first';
import last from 'lodash/last';
import sumBy from 'lodash/sumBy';
import isEmpty from 'lodash/isEmpty';
import round from 'lodash/round';
import { IPaymentScheduleBlockResponse } from '~/types/server.types';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import type { PaymentBlock } from '~/models/blocks/payment-block.model';
import type { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import { toFixedIfNotRound } from '~/utils/money.utils';
import { PaymentScheduleBlockErrors } from '~/types/error.types';
import { BlockInputValidationModel, TBlock } from '~/types/builder.types';
import { MIN_CLEARING_AMOUNT } from '~/common/flows.constants';
import { falseNotZero } from '@honeybook/hbui/dist/components/HBMoneyInput/HBMoneyInput.utils';
import { DateCalcType, DiscountTypes } from '~/types/model.types';
import { toCurrency } from '@honeybook/hbui/dist/components/HBCurrency/HBCurrency';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';

type PaymentScheduleBlockDataForServer = ReturnType<
    ContainerableBlock['getDataForServer']
> &
    Pick<IPaymentScheduleBlockResponse, 'type'>;

export class PaymentScheduleBlock extends ContainerableBlock {
    type: 'payment_schedule_block';
    @observable recurring_payments: boolean;

    constructor(
        block: IPaymentScheduleBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    get hoverable(): boolean {
        const {
            userStore: { currUser }
        } = this.rootStore;

        return !currUser.isSimplifiedInvoiceEnabled;
    }

    get isDeletable() {
        return false;
    }

    @computed
    get attributable() {
        return true;
    }

    @computed
    get totalPayments() {
        return sumBy(this.childBlocks, (payment: PaymentBlock) => {
            // If payment is partially refunded, add the relative amount to sum.
            if (payment.isPartiallyRefunded) {
                return payment.paymentAmount - payment.refundedAmount;
            }
            // If payment is not refunded, add to sum
            return !payment.isRefunded && payment.paymentAmount;
        });
    }

    @computed
    get totalAmount() {
        return sumBy(this.childBlocks, (payment: PaymentBlock) => {
            return payment.paymentAmount;
        });
    }

    @computed
    get hasPercentageBasedPayments() {
        return this.childBlocks.some((payment: PaymentBlock) => {
            return !payment.isFixed;
        });
    }

    @computed
    get totalPercents() {
        // the backend should return numbers with 2 decimal places
        // https://github.com/HoneyBook/HoneybookPaymentsDev/blame/hotfix/all/lib/infra/flow_payments_manager.rb#L213
        return round(
            sumBy(this.childBlocks, (payment: PaymentBlock) => {
                return payment.isFixed
                    ? 0
                    : payment.payment_content?.percentage;
            }),
            2
        );
    }

    @computed
    get shouldDisplayPaymentDateColumn() {
        return this.childBlocks.some((payment: PaymentBlock) => {
            return payment.paymentChargeDate;
        });
    }

    @computed
    get totalPaid() {
        return this.paymentBlocks.reduce((acc, paymentBlock) => {
            if (paymentBlock.isPaymentHandled) {
                return acc + paymentBlock.netPayAmount;
            }
            return acc;
        }, 0);
    }

    @computed
    get numberOfPaymentsPaid() {
        return this.paidPayments.length;
    }

    @computed
    get numberOfPaymentsWithFixedDate() {
        return this.paymentBlocks.filter(
            p => p?.payment_content?.date_calc_type === DateCalcType.Fixed
        ).length;
    }

    get numberOfPendingPayments() {
        return this.pendingPayments.length;
    }

    @computed
    get numberOfNonManualPaymentsPaid() {
        return this.nonManualPaidPayments.length;
    }

    @computed
    get numberOfManualPaymentsPaid() {
        return this.manualPaidPayments.length;
    }

    @computed
    get totalNumberOfPayments() {
        return this.paymentBlocks.length;
    }

    @computed
    get hasPaidPayments() {
        return this.numberOfPaymentsPaid > 0;
    }

    @computed
    get hasPendingPayments() {
        return this.numberOfPendingPayments > 0;
    }

    @computed
    get hasNonManualPaidPayments() {
        return this.numberOfNonManualPaymentsPaid > 0;
    }

    @computed
    get hasManualPaidPayments() {
        return this.numberOfManualPaymentsPaid > 0;
    }

    @computed
    get hasCompletedAllPayments() {
        return this.numberOfPaymentsPaid === this.totalNumberOfPayments;
    }

    @computed
    get isInvoiceInProgress() {
        return this.numberOfPaymentsPaid > 0 && !this.hasCompletedAllPayments;
    }

    @computed
    get numberOfPaymentsPaidWithoutPendingMAP() {
        return this.childBlocks.filter(
            paymentBlock =>
                (paymentBlock as PaymentBlock).isPaidWithoutPendingMAP
        ).length;
    }

    @computed
    get hasPaidPaymentsWithoutPendingMAP() {
        return this.numberOfPaymentsPaidWithoutPendingMAP > 0;
    }

    @computed
    get isDeleteDisabled() {
        return (
            this.hasPaidPaymentsWithoutPendingMAP ||
            this.hasPendingRefundOrUndoMAP ||
            this.hasRefundedPayments
        );
    }

    @computed
    get paymentBlocks() {
        return this.childBlocks as PaymentBlock[];
    }

    @computed
    get pendingMAPPayments() {
        return this.paymentBlocks.filter(
            paymentBlock => paymentBlock.isPendingMAP
        );
    }

    @computed
    get hasPendingRefundOrUndoMAP() {
        return (
            this.paymentBlocks.filter(
                paymentBlock => paymentBlock.isPendingRefundOrUndoMAP
            ).length > 0
        );
    }

    @computed
    get hasPendingMAPPayments() {
        return !!this.pendingMAPPayments.length;
    }

    @computed
    get paymentsBlocksMap() {
        const result = {};
        this.childBlocks.forEach((paymentBlock: PaymentBlock) => {
            result[paymentBlock.block_id] = paymentBlock;
        });
        return result;
    }

    @computed
    get firstPayment() {
        return first(this.paymentBlocks);
    }

    @computed
    get lastPayment() {
        return last(this.paymentBlocks);
    }

    @computed
    get upcomingPaymentBlock() {
        return this.flowRevision.upcomingPayment;
    }

    @computed
    get fixedPayments() {
        return this.paymentBlocks.filter(payment => payment.isFixed);
    }

    @computed
    get fixedUnpaidPayments() {
        return this.fixedPayments.filter(payment => !payment.isPaymentHandled);
    }

    @computed
    get unfixedPayments() {
        return this.paymentBlocks.filter(payment => !payment.isFixed);
    }

    @computed
    get fixedPaymentsAmount() {
        return sumBy(this.fixedPayments, payment => payment.paymentAmount);
    }

    @computed
    get fixedUnpaidPaymentsAmount() {
        return sumBy(
            this.fixedUnpaidPayments,
            payment => payment.paymentAmount
        );
    }

    @computed
    get hasFixedPayments() {
        return this.fixedPayments.length > 0;
    }

    @computed
    get nextPaymentBlock() {
        const nextPaymentBlock = this.childBlocks.find(
            (payment: PaymentBlock) => {
                return !payment.isPaid && !payment.isRefunded;
            }
        );
        return nextPaymentBlock as PaymentBlock;
    }

    @computed
    get paidPayments() {
        return this.paymentBlocks.filter(payment => payment.isPaymentHandled);
    }

    @computed
    get hasRefundedPayments() {
        return this.paymentBlocks.some(payment => payment.isRefunded);
    }

    @computed
    get pendingPayments() {
        return this.paymentBlocks.filter(payment => payment.isPending);
    }

    @computed
    get nonManualPaidPayments() {
        return this.paidPayments.filter(payment => !payment.isManualPaid);
    }

    @computed
    get manualPaidPayments() {
        return this.paidPayments.filter(payment => payment.isManualPaid);
    }

    isDraggable() {
        return false;
    }

    dndAcceptedTypes() {
        return null;
    }

    @computed
    get errors() {
        const {
            builderUiStore: { showErrors }
        } = this.rootStore;

        if (!showErrors) {
            return {};
        }

        const errors: any = {};
        if (!isEmpty(this.blockInputValidations)) {
            errors.hasErrors = true;
        }
        return errors;
    }

    get isRecurringInvoice() {
        return false;
    }

    @computed
    get invoiceBlock() {
        return this.parent as InvoiceBlock;
    }

    @computed
    get mobileErrorMessages() {
        const invoiceBlock = this.parent as InvoiceBlock;
        return [
            {
                enabled:
                    !this.blockInputValidations[
                        PaymentScheduleBlockErrors
                            .MissingMandatoryItemsForCustomPayment
                    ] &&
                    this.blockInputValidations[
                        PaymentScheduleBlockErrors.PaymentsExceedGrandTotal
                    ],
                message: `Payment plan is ${toCurrency(
                    this.totalPayments -
                        invoiceBlock.invoiceSummaryBlock.grandTotal
                )} more than the total.`
            },
            {
                enabled:
                    !this.blockInputValidations[
                        PaymentScheduleBlockErrors
                            .MissingMandatoryItemsForCustomPayment
                    ] &&
                    !this.blockInputValidations[
                        PaymentScheduleBlockErrors.PaymentsExceedGrandTotal
                    ] &&
                    !falseNotZero(
                        this.blockInputValidations[
                            PaymentScheduleBlockErrors
                                .FixedPaymentMoreThanMaximum
                        ]
                    ),
                message: `The custom payments, in total, cannot exceed ${toCurrency(
                    invoiceBlock.invoiceSummaryBlock.minimalUnpaidGrandTotal
                )}.`
            },
            {
                enabled:
                    !this.blockInputValidations[
                        PaymentScheduleBlockErrors
                            .MissingMandatoryItemsForCustomPayment
                    ] &&
                    this.blockInputValidations[
                        PaymentScheduleBlockErrors.PaymentsLessThanGrandTotal
                    ],
                message: `Payment plan is ${toCurrency(
                    invoiceBlock.invoiceSummaryBlock.grandTotal -
                        this.totalPayments
                )} less than the total.`
            },
            {
                enabled:
                    this.blockInputValidations[
                        PaymentScheduleBlockErrors.PercentsExceeded
                    ],
                message: `Sum of payment plan exceeds 100%. Make sure all %’s add up to 100%.`
            },
            {
                enabled:
                    this.blockInputValidations[
                        PaymentScheduleBlockErrors.PercentsFallShort
                    ],
                message: `Sum of payment plan is less than 100% of grand total. Make sure all %’s add up to 100%.`
            },
            {
                enabled:
                    this.blockInputValidations[
                        PaymentScheduleBlockErrors.MissingPercentageBasedPayment
                    ],
                message: `You must have at least one percentage based payment as your grand total depends on client's service selection.`
            },
            {
                enabled:
                    this.blockInputValidations[
                        PaymentScheduleBlockErrors
                            .MissingMandatoryItemsForCustomPayment
                    ],
                message: `To set custom payment amount, add a base or required service selection to the invoice.`
            }
        ];
    }

    @computed
    get blockInputValidations() {
        const validations: BlockInputValidationModel = {};
        const invoiceBlock = this.parent as InvoiceBlock;
        const { invoiceSummaryBlock } = invoiceBlock;
        const totalPaymentsSchedule = toFixedIfNotRound(this.totalPayments);
        const grandTotal = toFixedIfNotRound(
            invoiceBlock.invoiceSummaryBlock.grandTotal
        );

        const minimalUnpaidGrandTotal = toFixedIfNotRound(
            invoiceBlock.invoiceSummaryBlock.minimalUnpaidGrandTotal
        );
        const fixedPaymentsAmount = toFixedIfNotRound(this.fixedPaymentsAmount);

        const serviceSelectionComplete =
            !this.flow.invoiceContainerBlock.hasIncompleteServiceSelections;

        if (
            invoiceSummaryBlock.isClientPaymentPlan &&
            !invoiceBlock.hasPaidPayments
        ) {
            if (invoiceSummaryBlock.allowPayInFullDiscount) {
                if (
                    invoiceSummaryBlock.payInFullDiscountType ===
                        DiscountTypes.Relative &&
                    (invoiceSummaryBlock.payInFullDiscount > 100 ||
                        invoiceSummaryBlock.payInFullDiscount < 0)
                ) {
                    validations[
                        PaymentScheduleBlockErrors.PayInFullPercentageDiscountValidation
                    ] = true;
                }

                if (
                    invoiceSummaryBlock.payInFullDiscountType ===
                        DiscountTypes.Absolute &&
                    invoiceSummaryBlock.minimalUnpaidSubTotalAfterDiscount -
                        invoiceSummaryBlock.payInFullDiscount <
                        0
                ) {
                    validations[
                        PaymentScheduleBlockErrors.PayInFullFixedDiscountValidation
                    ] = true;
                }
            }
        } else {
            if (
                !invoiceBlock.hasPaidPayments &&
                !invoiceBlock.hasPendingPayments
            ) {
                if (invoiceBlock.hasSelection) {
                    if (!this.hasPercentageBasedPayments) {
                        validations[
                            PaymentScheduleBlockErrors.MissingPercentageBasedPayment
                        ] = true;
                    } else {
                        if (this.totalPercents > 100) {
                            validations[
                                PaymentScheduleBlockErrors.PercentsExceeded
                            ] = true;
                        } else if (this.totalPercents < 100) {
                            validations[
                                PaymentScheduleBlockErrors.PercentsFallShort
                            ] = true;
                        }
                    }
                }
                if (
                    isEmpty(invoiceBlock.invoiceFixedItems) &&
                    !invoiceBlock.hasRequiredSelection &&
                    this.hasFixedPayments
                ) {
                    // has no fixed and no selection
                    validations[
                        PaymentScheduleBlockErrors.MissingMandatoryItemsForCustomPayment
                    ] = true;
                } else if (fixedPaymentsAmount > minimalUnpaidGrandTotal) {
                    validations[
                        PaymentScheduleBlockErrors.FixedPaymentMoreThanMaximum
                    ] = Boolean(minimalUnpaidGrandTotal);
                }
            }

            if (
                grandTotal >= minimalUnpaidGrandTotal &&
                totalPaymentsSchedule > grandTotal
            ) {
                validations[
                    PaymentScheduleBlockErrors.PaymentsExceedGrandTotal
                ] = true;
            } else if (totalPaymentsSchedule < grandTotal) {
                validations[
                    PaymentScheduleBlockErrors.PaymentsLessThanGrandTotal
                ] = true;
            }

            if (
                grandTotal > 0 &&
                !invoiceBlock.hasSelection &&
                this.paymentBlocks
                    .filter(paymentBlock => !paymentBlock.isPaid)
                    .some(
                        unPaidPaymentBlock =>
                            unPaidPaymentBlock.paymentAmount <
                                MIN_CLEARING_AMOUNT &&
                            serviceSelectionComplete &&
                            unPaidPaymentBlock.isPercentageBased
                    )
            ) {
                validations[PaymentScheduleBlockErrors.PaymentLessThanMinimum] =
                    true;
            }
        }

        return validations;
    }

    @computed
    get getAmountError() {
        const {
            [PaymentScheduleBlockErrors.PaymentsExceedGrandTotal]:
                paymentsExceedGrandTotal,
            [PaymentScheduleBlockErrors.FixedPaymentMoreThanMaximum]:
                fixedMoreThanMaximum
        } = this.blockInputValidations;

        const fixedPaymentExceededItsLimit =
            !falseNotZero(fixedMoreThanMaximum);

        if (paymentsExceedGrandTotal && fixedPaymentExceededItsLimit) {
            const invoiceBlock = this.parent as InvoiceBlock;
            const minimalUnpaidGrandTotal = toFixedIfNotRound(
                invoiceBlock.invoiceSummaryBlock.minimalUnpaidGrandTotal
            );
            const grandTotal = toFixedIfNotRound(
                invoiceBlock.invoiceSummaryBlock.grandTotal
            );

            const displayGrandTotalError =
                grandTotal === minimalUnpaidGrandTotal &&
                !invoiceBlock.hasIncompleteServiceSelections;

            return displayGrandTotalError ? 'GRAND_TOTAL' : 'FIXED_PAYMENTS';
        }

        if (paymentsExceedGrandTotal) {
            return 'GRAND_TOTAL';
        }

        if (fixedPaymentExceededItsLimit) {
            return 'FIXED_PAYMENTS';
        }
        return null;
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);
        super.mergeChildrenAttributes(block.children);
    }

    canBeParentOf(block: TBlock) {
        return block.type === 'payment_block';
    }

    getDataForServer(): PaymentScheduleBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'payment_schedule_block'
        };
    }
}
